import { useSalesOrderControllerGetSalesOrderWorkOrders } from '@/generated/api/sales-order.ts';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import type { SalesOrderWorkOrderDto } from '@/generated/api/pacApi.schemas.ts';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import AnchorButton from '@/common/components/AnchorButton.tsx';
import WorkOrderStatusBadge from '@/features/work-orders/components/WorkOrderStatusBadge.tsx';

export default function SalesOrderWorkOrdersTabBody({
  salesOrderId,
}: {
  salesOrderId: string;
}) {
  const workOrdersQuery =
    useSalesOrderControllerGetSalesOrderWorkOrders(salesOrderId);

  return (
    <Tabs.Panel
      value="work-orders"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {workOrdersQuery.isLoading ? (
        <ScrollArea
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack gap="sm">
            <Skeleton height={32} radius="md" />
            <Skeleton height={118} radius="md" />
            <Skeleton height={176} radius="md" />
          </Stack>
        </ScrollArea>
      ) : workOrdersQuery.isError ? (
        <ScrollArea
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Alert color="red" title="Sales order failed to load">
            <Stack gap="sm">
              <Text size="sm">
                Check your connection and retry. If this continues, return to
                the sales order list and select the item again.
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => void workOrdersQuery.refetch()}
              >
                Retry
              </Button>
            </Stack>
          </Alert>
        </ScrollArea>
      ) : (
        <ScrollArea style={{ height: '100%' }}>
          <Stack gap="sm">
            {workOrdersQuery.data?.workOrders.length ? (
              workOrdersQuery.data.workOrders.map((workOrder) => (
                <SalesOrderWorkOrderCard
                  key={workOrder.id}
                  workOrder={workOrder}
                />
              ))
            ) : (
              <Alert color="gray" title="No work orders">
                This sales order does not have any associated work orders.
              </Alert>
            )}
          </Stack>
        </ScrollArea>
      )}
    </Tabs.Panel>
  );
}

function SalesOrderWorkOrderCard({
  workOrder,
}: {
  workOrder: SalesOrderWorkOrderDto;
}) {
  const sidebarStack = useSidebarStack();

  return (
    <Paper p="sm" withBorder radius="md">
      <Stack gap={8}>
        <Group gap="xs" justify="space-between">
          {workOrder.externalId ? (
            <AnchorButton
              onClick={() =>
                sidebarStack.pushItem('workOrderDetails', {
                  workOrderId: workOrder.id,
                  workOrderExternalId: workOrder.externalId!,
                })
              }
            >
              {`WO #${workOrder.externalId}`}
            </AnchorButton>
          ) : (
            <Text size="sm" fw={400} c="dimmed">
              Unknown WO #
            </Text>
          )}

          <WorkOrderStatusBadge status={workOrder.status} />
        </Group>
        <Group gap="xs" justify="space-between">
          <Text fw={500} size="sm">
            {workOrder.clientName}
          </Text>
          <Badge size="sm" radius="xl" variant="light" tt="none">
            {workOrder.workOrderType}
          </Badge>
        </Group>
        <Group gap="xs" justify="space-between">
          <Text fw={500} size="sm">
            {workOrder.projectName}
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}
